import React, { useEffect, useState } from 'react';
import AddButton from '../../../../Components/Button/AddButton';
import styles from './MedicalRequestsListWrapper.module.scss';
import {
	Document,
	DocumentType,
	useDocumentsQuery,
} from '../../../../graphql/graphql';
import MedicalRequestCreateModal from '../MedicalRequestCreate/MedicalRequestCreateModal';
import useScope from '../../../Scope/Hooks/useScope';
import MedicalRequestPolandModal from '../../../Scope/Poland/Components/MedicalRequestPoland/MedicalRequestPolandModal';
import Text from '../../../../Components/Base/Text/Text';
import useJob from '../../../Base/Hooks/useJob';
import { useFlatDataList } from '../../../Base/Hooks/useFlatDataList';
import MedicalRequestsList from './MedicalRequestsList';

interface Props {
	patientId: string;
	workplaceId: string;
	setIsLoading: (isLoading: boolean) => void;
}

const MedicalRequestsListWrapper: React.FC<Props> = ({
	patientId,
	workplaceId,
	setIsLoading,
}) => {
	const { isPoland } = useScope();
	const { handleStartWaitingForJob, RenderJobWaiting } = useJob();
	const [isCreateRequestOpen, setIsCreateRequestOpen] = useState(false);

	const { flatData, pagination, getNextPage, appendData, updateItem, reset } =
		useFlatDataList<Document>();

	const { data, isLoading, refetch, isFetching } = useDocumentsQuery({
		userId: patientId,
		workplaceId,
		types: [DocumentType.MedicalRequestForm, DocumentType.MedicalRequestP1],
		...pagination,
	});

	useEffect(() => {
		appendData((data?.documents?.data as Document[]) ?? []);
	}, [data]);

	const refresh = () => {
		reset();
		refetch();
	};

	const handleCreateWithJobSuccess = (jobId?: string) => {
		setIsCreateRequestOpen(false);

		if (jobId) {
			return handleStartWaitingForJob(jobId);
		}

		return handleRefreshData();
	};

	const handleRefreshData = async () => {
		setIsLoading(true);
		refresh();
		setIsLoading(false);
	};

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.options}>
					<AddButton
						title='component.medicalRequestForms.createARequisition'
						onClick={() => setIsCreateRequestOpen(true)}
					/>
				</div>
				<div className={styles.header}>
					<Text
						className={styles.date}
						tx='component.medicalReportList.header.date'
					/>
					<Text
						className={styles.document}
						tx='component.medicalReportList.header.document'
					/>
				</div>
				<MedicalRequestsList
					data={flatData}
					isLoading={isLoading || isFetching}
					onRefresh={refresh}
					onLoadMore={getNextPage}
					hasMoreData={data?.documents?.meta?.hasNextPage || false}
				/>
				{isCreateRequestOpen && (
					<>
						{isPoland ? (
							<MedicalRequestPolandModal
								patientId={patientId}
								isOpen
								onClose={() => setIsCreateRequestOpen(false)}
								onSuccess={handleCreateWithJobSuccess}
							/>
						) : (
							<MedicalRequestCreateModal
								isOpen
								setIsOpen={setIsCreateRequestOpen}
								patientId={patientId}
								onCreate={handleCreateWithJobSuccess}
							/>
						)}
					</>
				)}
			</div>
			<RenderJobWaiting onJobSuccess={handleRefreshData} timeout={15000} />
		</>
	);
};

export default MedicalRequestsListWrapper;
